import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import Media from 'react-media';

import { useWindowScrollPosition } from '../../hooks';
import { ArrowDown } from '../../icons';
import { theme } from '../../theme';
import Grid from '../Grid';
import {
  StyledArrow,
  StyledButton,
  StyledLink,
  StyledList,
  StyledPanel,
  StyledRefDiv,
  StyledStaticWrapper,
  StyledWrapper,
} from './ContentIndex.styles';
import { IContentIndexProps } from './ContentIndex.types';
import ContentIndexItem from './ContentIndexItem';

export const ContentIndexContext = createContext<HTMLElement[]>([]);

const ContentIndex = ({
  ariaLabel = 'Show/hide',
  className,
  href,
  items,
  linkText,
  tag = 'ul',
  threshold = 100,
  user = 'neutral',
}: IContentIndexProps): JSX.Element => {
  const { y } = useWindowScrollPosition();
  const refs: HTMLElement[] = useContext(ContentIndexContext);
  const edgeRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [open, setOpen] = useState<boolean>(false);

  const contents = refs.map((item: HTMLElement, index: number) => {
    if (item) {
      const currentOffset = item.offsetTop - threshold;
      const nextOffset = refs[index + 1]
        ? refs[index + 1].offsetTop - threshold
        : null;
      return {
        currentOffset,
        nextOffset,
      };
    }

    return null;
  });

  useEffect(() => {
    contents.forEach((content, index) => {
      if (content) {
        const { currentOffset, nextOffset } = content;

        if (
          (nextOffset && y >= currentOffset && y < nextOffset) ||
          y >= currentOffset
        ) {
          setActiveIndex(index);
        }
      }
    });
  }, [contents, y]);

  return (
    <>
      <Media
        queries={{
          mobile: `(max-width: calc(${theme.breakpoints.l} - 1px))`,
          desktop: `(min-width: ${theme.breakpoints.l})`,
        }}
        defaultMatches={{ desktop: true }}
      >
        {matches => (
          <>
            {matches.mobile && (
              <>
                <StyledStaticWrapper className={className}>
                  <Grid container direction="column">
                    <StyledList as={tag}>
                      {items.map(item => {
                        const { id, text } = item;

                        return (
                          <ContentIndexItem
                            active={false}
                            id={id}
                            text={text}
                            user={user}
                            key={id}
                          />
                        );
                      })}
                    </StyledList>
                  </Grid>
                </StyledStaticWrapper>
                <StyledRefDiv ref={edgeRef} />
                {y >= (!!edgeRef.current && edgeRef.current.offsetTop + 24) && (
                  <StyledWrapper active={open} className={className}>
                    <StyledButton
                      aria-controls="contentindexPanel"
                      aria-expanded={open}
                      aria-label={ariaLabel}
                      id="contentindex"
                      role="button"
                      user={user}
                      onClick={() => setOpen(!open)}
                    >
                      <Grid container justifyContent="space-between">
                        {items.map(
                          ({ text }, index) => index === activeIndex && text,
                        )}
                        <StyledArrow>
                          <ArrowDown />
                        </StyledArrow>
                      </Grid>
                    </StyledButton>
                    <StyledPanel
                      id="contentindexPanel"
                      aria-hidden={!open}
                      aria-labelledby="contentindex"
                      role="region"
                    >
                      {open && (
                        <Grid container direction="column">
                          <StyledList as={tag}>
                            {items.map((item, index) => {
                              const { id, text } = item;

                              return (
                                <ContentIndexItem
                                  active={index === activeIndex}
                                  id={id}
                                  text={text}
                                  user={user}
                                  key={id}
                                  onClick={() => setOpen(!open)}
                                />
                              );
                            })}
                          </StyledList>
                        </Grid>
                      )}
                    </StyledPanel>
                  </StyledWrapper>
                )}
              </>
            )}
            {matches.desktop && (
              <StyledWrapper active={open} className={className}>
                <StyledList as={tag}>
                  {items.map((item, index) => {
                    const { id, text } = item;

                    return (
                      <ContentIndexItem
                        active={index === activeIndex}
                        id={id}
                        text={text}
                        user={user}
                        key={id}
                      />
                    );
                  })}
                </StyledList>
                <StyledLink href={href} title={linkText} user={user}>
                  {linkText}
                </StyledLink>
              </StyledWrapper>
            )}
          </>
        )}
      </Media>
    </>
  );
};

export default ContentIndex;
